import { ArrowLeftIcon } from '@heroicons/react/20/solid';
import { FC, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { FaEnvelope, FaUser } from 'react-icons/fa';
import { useContextSelector } from 'use-context-selector';

import { rootElementSelector } from '@/core/store/global/app-states';
import { AppContext } from '@/core/store/global/AppProvider';
import { productSelector } from '@/core/store/productContext/product-states';
import { appendImageRepoDomainIfNotExistInUrl } from '@/core/utils/helpers';
import { appSettings } from '@/settings/app-settings';
import { CountryPicker001 } from '@/ux-legacy/uikit/elements/elements-index';

type UserDetailsFormProps = {
  onClose: () => void;
  isLoading: boolean;
  onSubmit: (details: { name: string; phone: string; email: string }) => void;
  products?: any[];
  title?: string;
};

const defaultCountryPick = {
  name: 'Norway',
  flag: '🇳🇴',
  code: 'NO',
  dial_code: '+47',
};

const UserDetailsForm: FC<UserDetailsFormProps> = ({
  onClose,
  isLoading,
  onSubmit,
  products,
  title = 'Place your order',
}) => {
  const [name, setName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const rootElement = useContextSelector(AppContext, rootElementSelector);
  const productState = useContextSelector(AppContext, productSelector);
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [countryCode, setCountryCode] = useState<string>('');
  const [validateRecaptcha, setValidateRecaptcha] = useState(false);
  const [validateRecaptchaMessage, setValidateRecaptchaMessage] = useState(false);
  const googleSiteKey = appSettings.api.GOOGLECAPTCHASITEKEY;
  const [country, OnCountryCodeChange] = useState(defaultCountryPick);

  function handleOnCaptchaChange() {
    setValidateRecaptcha(true);
    setValidateRecaptchaMessage(false);
  }

  return (
    <div className="fixed inset-0 flex items-center justify-center z-[99999]">
      <div className="absolute bg-gray-500 opacity-75 inset-0"></div>
      <div className="bg-white w-[90%] h-auto md:w-[400px] rounded shadow-lg p-4 absolute ">
        <div className="text-lg font-medium  w-full  mb-4">
          <div className="flex justify-between ">
            <ArrowLeftIcon className="w-6 h-6 " onClick={onClose} />
            <div className="">{title}</div>
            <div></div>
          </div>
        </div>
        <hr />
        <div className="overflow-y-auto overflow-x-hidden h-auto py-2 no-scrollbar">
          {products?.map((product: any, index: number) => {
            return (
              <div className="flex justify-center  my-5" key={index}>
                <div>
                  <div className="flex flex-row min-h-[100px]  w-full">
                    <div className="h-20 w-20 flex-shrink-0 overflow-hidden rounded-2xl border border-gray-200">
                      <a>
                        <img
                          src={appendImageRepoDomainIfNotExistInUrl(product.image)}
                          alt={product.title}
                          className="h-full w-full object-cover object-center"
                        />
                      </a>
                    </div>

                    <div className="ml-4 flex flex-1 flex-col relative w-[60%] ">
                      <a>
                        <div className="text-base font-bold text-gray-700">
                          <h3 className="text-sm line-clamp-2 break-all">{product.title}</h3>
                        </div>
                      </a>

                      <div className="flex flex-1 justify-between">
                        <div>
                          <div className="flex">
                            {product?.version?.option1 && (
                              <div>
                                {/* {product?.versionDetails?.option1?.label}: */}
                                {/* <span className="ml-1"> */}
                                {product?.version?.option1?.value}
                                {/* </span> */}
                              </div>
                            )}
                            {product?.version?.option2 && (
                              <div>
                                {product?.version?.option2?.label}:
                                <span className="ml-1">{product?.version?.option2?.value}</span>
                              </div>
                            )}
                            {product?.versionDetails?.option3 && (
                              <div>
                                {product?.version?.option3?.label}:
                                <span className="ml-1">{product?.version?.option3?.value}</span>
                              </div>
                            )}
                            {product?.version?.duration && (
                              <div className="mt-1 text-sm">
                                Duration:
                                <span className="ml-1">{product?.version?.duration?.label}</span>
                              </div>
                            )}
                          </div>
                          <div className="flex gap-2">
                            {product.quantity?.quantity && (
                              <p className="mt-1 text-sm font-bold text-gray-400 ">
                                {product?.quantity?.key} {product?.quantity?.value}
                              </p>
                            )}
                            {product.adults?.value && (
                              <p className="mt-1 text-sm text-gray-400 capitalize">
                                {product.adults.key} {product.adults.value}
                              </p>
                            )}
                            <br />
                            {product.children?.value && (
                              <p className="mt-1 ml-1 text-sm text-gray-400 capitalize">
                                {product.children.key} {product.children.value}
                              </p>
                            )}
                          </div>
                          {product.startDate && (
                            <div className="flex flex-col mt-1 mb-1">
                              <p className="text-sm font-bold text-gray-400">{product.startDate}</p>

                              <p className="text-sm text-gray-400">{product.startTime}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
          <form
            onSubmit={async (event) => {
              event.preventDefault();
              onSubmit({
                name: name,
                email: email,
                phone: country.dial_code + ' ' + phoneNumber,
              });
            }}
            className="space-y-4"
          >
            <div className="md:px-5">
              <label htmlFor="name" className="text-gray-800 block mb-1 font-bold">
                Contact Information
              </label>
              <div className="relative">
                <FaUser className="absolute top-3 left-3 text-gray-500 mt-0.5" />
                <input
                  id="name"
                  required
                  type="text"
                  className="w-full pl-10  border rounded-t-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-black"
                  value={name}
                  placeholder="Full Name"
                  autoComplete="off"
                  onChange={(e) => {
                    const inputValue = e.target.value.trimStart();
                    setName(inputValue);
                  }}
                  style={{ paddingLeft: '40px' }}
                />
              </div>
              <div className="relative">
                <FaEnvelope className="absolute top-3 left-3 text-gray-500 mt-0.5" />
                <input
                  id="email"
                  required
                  type="email"
                  className="w-full pl-10 border-gray-300 border  px-3 py-2 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-black"
                  value={email}
                  placeholder="email@example.com"
                  autoComplete="off"
                  onChange={(e) => {
                    const inputValue = e.target.value.trimStart();
                    setEmail(inputValue);
                  }}
                  style={{ paddingLeft: '40px' }}
                />
              </div>
              <div className="flex relative items-center rounded-b-md focus:outline-none focus:ring-2 focus:ring-inset focus:ring-black">
                <input
                  id="businessContactNumber"
                  className="w-full px-1 py-2 pl-28 border-gray-300 rounded-b-md border focus:outline-none focus:ring-2 focus:ring-inset focus:ring-black"
                  placeholder="1234567"
                  type="number"
                  inputMode="numeric"
                  autoComplete="off"
                  onChange={(e) => {
                    const inputValue = e.target.value;
                    const numericValue = inputValue.replace(/[^0-9]/g, '');
                    setPhoneNumber(numericValue);
                  }}
                  style={{ paddingLeft: '112px' }}
                />
                <div className="absolute top-0 left-0 ">
                  <CountryPicker001
                    onChange={(country: any) => {
                      OnCountryCodeChange(country);
                    }}
                    rootElement={rootElement}
                    selected={country}
                    classNames=""
                  />
                </div>
              </div>
            </div>
            {/* Other form fields */}
            {/* ... */}
            <div className="flex justify-center items-center">
              <ReCAPTCHA sitekey={googleSiteKey || ''} onChange={handleOnCaptchaChange} />
            </div>
            {/* Display recaptcha validation message */}
            {validateRecaptchaMessage && (
              <div className="flex justify-center">
                <p className="text-sm font-medium text-red-600">
                  <span className="mr-1">⚠</span>
                  Please verify that you are not a robot.
                </p>
              </div>
            )}
            <div className="flex justify-center md:px-7">
              <button
                type="submit"
                className={`py-2 w-full rounded-full ${
                  isLoading
                    ? 'bldr-cta-button cursor-not-allowed animate-pulse'
                    : 'bldr-cta-button hover:bg-slate-800'
                } text-white mr-2`}
                disabled={isLoading}
              >
                {isLoading ? 'Loading...' : 'Submit'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UserDetailsForm;
